import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import B2BLayout from '../../../layouts/b2b'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { DefaultJsonForm } from '../../../components/v2/default-json-form'
import { Alert, Skeleton, Button, Tabs, Form, Checkbox } from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { MapContent, MapTitle, MapWrapper, AlertWrapper, TaskWrapper } from './styles'
import { defaultSelectedSchema } from './json-schema'
import { objectToUrl, renameKeys, setEnumFields } from '../../utils'
import TabReport from '../../../pages/card-organization/tab-report'
import RatingDiler from '../../../connectors/rating-diler'
import TabRequisites from './tabs/requisite'
import TabUsers from './tabs/users'
import TabMail from './tabs/mail'
import { IBreadcrumbs } from './types'
import ContractNumberGenerator from './contract-number-genetartor'

export default function AdminNewObjectPage() {
  const { id, organization_id } = useParams() as any
  const navigate = useNavigate()
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true)
  const [pageErrors, setPageErrors] = useState<string[]>([])
  const [pageSuccess, setPageSuccess] = useState<string[]>([])
  const [formData, setFormData] = useState<any>({})
  const [matchedJSONSchema, setMatchedJSONSchema] = useState<any>(null)
  const [selectedUsers, setSelectedUsers] = useState<any[]>([])
  const [organization, setOrganization] = useState<any>({})
  const [breadcrumbs, setBreadcrumbs] = useState<IBreadcrumbs[]>([
    { href: '/admin', title: 'Админка' },
    { href: '/new/organizations', title: 'Список организаций' },
  ])
  const [useContractTemplate, setUseContractTemplate] = useState<boolean>(false)
  const [contractNumberTemplate, setContractNumberTemplate] = useState<string>('')

  const saveCase = useCallback(async () => {
    const data = { ...formData }

    // Добавляем шаблон номера договора в данные формы
    if (useContractTemplate) {
      data.contract_number_template = contractNumberTemplate
    } else {
      delete data.contract_number_template
    }

    await fetchAPI(`/api/office/${id ? id : 'new'}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(result => {
      if (result?.error) {
        const errors =
          Array.isArray(result['errors']) && result['errors']?.length > 0
            ? result['errors'].map(error => error['detail'])
            : ['Неизвестная ошибка при выполнении запроса']
        setPageSuccess([])
        setPageErrors(errors)
      } else {
        navigate(!id ? `/office/${result.id}` : `/offices/${organization.id}`)
      }
    })
  }, [history, organization, id, formData, contractNumberTemplate])

  const getData = useCallback(async () => {
    if (id) {
      const { data, organization: org } = await fetchAPI(`/api/office/${id}`)
      const fData = data.reduce(
        (obj, cur) => ({
          ...obj,
          [cur.name]: Array.isArray(cur.value) && !cur.value[0] ? [] : cur.value,
        }),
        {},
      )
      setOrganization(org)

      // Устанавливаем состояние шаблона на основе данных из API
      if (fData.contract_number_template) {
        setContractNumberTemplate(fData.contract_number_template)
        setUseContractTemplate(true)
      }
      setFormData({
        ...fData,
        organization_id: parseInt(org.id),
      })
      const enumsList = { types: ['organizations', 'case_region', 'avito_cities'] }
      const enums = await fetchAPI(`/api/enum-list${objectToUrl(enumsList)}`)
      const offices = await fetchAPI('/api/get-task-responsible-offices').then(result =>
        result.filter(v => v.id !== +id).map(v => ({ id: v.id, value: v.name })),
      )
      const orgName = enums.organizations.find(v => +v.id === +org.id)?.value
      const orgId = parseInt(org.id)

      setBreadcrumbs(prev => [
        ...prev,
        { href: `/offices/${orgId}`, title: `Список офисов ${orgName}` },
      ])

      const fixKeys = renameKeys(enums, {
        organizations: 'organization_id',
        case_region: 'region_id',
      })
      setEnumFields(
        {
          ...fixKeys,
          additional_offices_in_task: offices,
        },
        defaultSelectedSchema,
      )
      setMatchedJSONSchema(defaultSelectedSchema)
    } else if (organization_id) {
      setFormData({ organization_id: Number(organization_id) })
      const enumsList = { types: ['organizations', 'case_region', 'avito_cities'] }
      const enums = await fetchAPI(`/api/enum-list${objectToUrl(enumsList)}`)
      const offices = await fetchAPI('/api/get-task-responsible-offices').then(result =>
        result.filter(v => v.id !== +id).map(v => ({ id: v.id, value: v.name })),
      )
      const orgName = enums.organizations.find(v => +v.id === +organization_id)?.name

      setBreadcrumbs(prev => [
        ...prev,
        { href: `/offices/${organization_id}`, title: `Список офисов ${orgName}` },
      ])

      const fixKeys = renameKeys(enums, {
        organizations: 'organization_id',
        case_region: 'region_id',
      })
      setEnumFields(
        {
          ...fixKeys,
          additional_offices_in_task: offices,
        },
        defaultSelectedSchema,
      )
      setMatchedJSONSchema(defaultSelectedSchema)
    }
    setPageErrors([])
    setIsDataLoading(false)
  }, [id, organization_id, setContractNumberTemplate, setUseContractTemplate])

  useEffect(() => {
    getData().catch(e => {
      console.error({ e })
    })
  }, [getData])

  const onFormData = useCallback(
    ({ formData }) => {
      setFormData({
        ...formData,
        district: formData?.address?.federal_district || formData?.district,
        address: formData?.address?.address || formData?.address,
        region: formData?.address?.region || formData?.region,
        city: formData?.address?.city || formData?.city,
      })
    },
    [setFormData],
  )

  const onTableSelected = useCallback(selected => setSelectedUsers(selected), [])

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      <MapWrapper>
        <TaskWrapper>
          <MapTitle>
            {id === 'new' ? (
              <>Добавление офиса</>
            ) : (
              <>
                Офис <span>{formData.name}</span>
              </>
            )}
          </MapTitle>
        </TaskWrapper>
        <MapContent>
          {pageErrors.length > 0 && (
            <AlertWrapper>
              <Alert
                message="При выполнении операции возникла ошибка:"
                showIcon
                type="error"
                description={pageErrors.join('. ')}
              />
            </AlertWrapper>
          )}
          {pageSuccess.length > 0 && (
            <div style={{ marginBottom: 40 }}>
              <Alert message={pageSuccess.join('. ')} showIcon type="success" />
            </div>
          )}
          {!isDataLoading ? (
            <>
              <DefaultJsonForm
                formData={formData}
                schema={matchedJSONSchema}
                onChange={onFormData}
                orientation="horizontal"
              />
              <Form>
                <Form.Item label="Использовать шаблоны для номеров договоров">
                  <Checkbox
                    onChange={e => {
                      setUseContractTemplate(e.target.checked)
                      if (!e.target.checked) {
                        setContractNumberTemplate('')
                      }
                    }}
                    disabled={!formData.requisites_count}
                    name="numberTemplate"
                    checked={formData.requisites_count ? useContractTemplate : false}
                  />
                </Form.Item>
              </Form>
              {useContractTemplate && (
                <ContractNumberGenerator
                  sequenceNumber={formData?.contract_number}
                  initialTemplate={contractNumberTemplate}
                  onTemplateChange={value => {
                    setContractNumberTemplate(value)
                  }}
                />
              )}
              {id && (
                <Tabs
                  type="card"
                  items={[
                    {
                      key: 'users',
                      label: 'Пользователи',
                      children: <TabUsers id={id} org_id={organization?.id} />,
                    },
                    {
                      key: 'report',
                      label: 'Расходы и доходы',
                      children: <TabReport />,
                    },
                    {
                      key: 'rating',
                      label: 'Рейтинг',
                      children: <RatingDiler id={id} />,
                    },
                    {
                      key: 'requisites',
                      label: 'Банковские реквизиты',
                      children: <TabRequisites id={id} />,
                    },
                    {
                      key: 'mail',
                      label: 'Почта',
                      children: <TabMail onChange={onTableSelected} id={id} />,
                    },
                  ]}
                />
              )}
            </>
          ) : (
            <Skeleton active />
          )}
          <Button onClick={saveCase} style={{ marginBottom: 16, marginTop: 16 }} type="primary">
            <SaveOutlined />
            Сохранить
          </Button>
        </MapContent>
      </MapWrapper>
    </B2BLayout>
  )
}
